import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import {
  AppButton,
  Box,
  ErrorModal,
  RoundDoneIcon,
  Text,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { usePlaid } from '@/utils/plaid/hooks/usePlaid';
import PlaidModal from '@/utils/plaid/PlaidModal';
import { useDuplicatePlaidAccountStore } from '@/utils/plaid/store';

import useAddPlaidPaymentMethod from '../../ReimburseMe/queries/useAddPlaidPaymentMethod.query';

export enum ModalSteps {
  BANK_FORM = 'BANK_FORM',
  SUCCESS = 'SUCCESS',
}

interface Props {
  visible: boolean;
  onSetVisible: (value: boolean) => void;
  setCurrentBankAccount?: (value: string) => void;
  isPendingAccountExists?: boolean;
  refetch?: () => void;
}

export const AddContributionBankAccountModal: React.FC<Props> = ({
  visible,
  onSetVisible,
  setCurrentBankAccount,
  isPendingAccountExists,
  refetch,
}) => {
  const [step, setStep] = useState<ModalSteps>(ModalSteps.BANK_FORM);
  const [errorModal, setErrorModal] = useState(false);
  const [isPlaidLoading, setIsPlaidLoading] = useState(false);
  const { t } = useTranslation();
  const handleSetVisible = useCallback(() => {
    onSetVisible(false);
    setIsPlaidLoading(false);
    setStep(ModalSteps.BANK_FORM);
    refetch?.();
  }, [
    onSetVisible,
    refetch,
  ]);

  const handleSetCurrentBankAccount = useCallback((value?: string, values?: string[]) => {
    if (setCurrentBankAccount) {
      if (values && values.length) setCurrentBankAccount(values[0]);
      if (value) setCurrentBankAccount(value);
      setStep(ModalSteps.SUCCESS);
    }
  }, [setStep, setCurrentBankAccount]);

  const { save } = useAddPlaidPaymentMethod(handleSetCurrentBankAccount);

  const setDuplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.setIsDuplicateModalVisible);

  const handlePlaidPaymentMethodSave = useCallback(async (plaidMetadata: PlaidLinkOnSuccessMetadata) => {
    try {
      setIsPlaidLoading(true);
      await save({
        plaidMetadata,
      });
      setIsPlaidLoading(false);
    } catch (e) {
      // @ts-ignore
      if (e.response.status === 409) {
        setDuplicateAccountModal(true);
        return;
      }
      setErrorModal(true);
    }
  }, [save, setDuplicateAccountModal]);

  const { openPlaid, content } = usePlaid(
    {
      onSuccess: handlePlaidPaymentMethodSave,
      onExit: handleSetVisible,
      isPendingAccountExists,
    },
  );

  useEffect(() => {
    if (visible && step === ModalSteps.BANK_FORM) {
      openPlaid();
    }
  }, [visible, openPlaid, step]);

  return (
    <>
      {step === ModalSteps.BANK_FORM && content}

      {isPlaidLoading && (
        <PlaidModal
          visible
          isLoading
          onSetVisible={() => setIsPlaidLoading(false)}
        />
      )}

      {step === ModalSteps.SUCCESS && (
        <ModalWrapper testId="AddContributionBankAccount" visible={visible} onSetVisible={handleSetVisible}>
          <Box align="center" pad={{ vertical: 'spacing24' }}>
            <Box width="xxsmall" alignSelf="center">
              <RoundDoneIcon />
            </Box>

            <Text
              size="2xl"
              weight="bold"
              color="textBody"
              margin={{ top: 'spacing12', bottom: 'spacing24' }}
            >
              {t('The Payment Method Was Successfully Added')}
            </Text>
            <AppButton
              type="button"
              onClick={handleSetVisible}
              width="200px"
            >
              {t('Got It!')}
            </AppButton>
          </Box>
        </ModalWrapper>
      )}
      {/* <DuplicationErrorModal
        visible={isErrorVisible}
        close={() => setIsErrorVisible(false)}
      /> */}
      <ErrorModal
        testId="payment-methods-error-modal"
        visible={errorModal}
        onSetVisible={() => {
          setErrorModal(false);
          handleSetVisible();
        }}
        header={t('Something went wrong')}
        helpText={t('Try again later')}
        buttonText={t('Got It!')}
        onSubmit={() => {
          setErrorModal(false);
          handleSetVisible();
        }}
      />
    </>
  );
};
